import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { Bootstrap, Tab } from './types'
import { LangProvider } from './lang'
import { FollowProvider } from './favourites'
import { Masthead } from './components/Masthead'
import { Footer } from './components/Footer'
import { LiveTab } from './components/LiveTab'
import { JournalTab } from './components/JournalTab'
import { NotesTab } from './components/NotesTab'
import { StatsTab } from './components/StatsTab'
import { DirectoryTab } from './components/DirectoryTab'
import { AboutTab } from './components/AboutTab'
import { SourcesTab } from './components/SourcesTab'
import { StationPageTab } from './components/StationPageTab'
import { LetterTab } from './components/LetterTab'
import { SpeciesModal } from './components/SpeciesModal'
import { AccountPanel } from './components/AccountPanel'
import { AdminPanel } from './components/AdminPanel'
import * as shell from './components/shell.css'

type Panel = 'account' | 'admin' | null

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 } },
})

const TABS: Tab[] = [
  'live',
  'journal',
  'notes',
  'stats',
  'directory',
  'about',
  'sources',
  'tech',
  'privacy',
  'letter',
]

// A permalinked day: /journal/2026-07-24 → that ISO date; /journal → null (yesterday, the default).
const JOURNAL_PATH = /^\/journal(?:\/(\d{4}-\d{2}-\d{2}))?\/?$/
function journalDateFromPath(): string | null {
  return window.location.pathname.match(JOURNAL_PATH)?.[1] ?? null
}

// A permalinked note: /notes/the-herring-nights → that slug; /notes → null (the index). Same shape
// as the Journal's date, and the same reason: the thing people share is the URL.
const NOTES_PATH = /^\/notes(?:\/([a-z0-9]+(?:-[a-z0-9]+)*))?\/?$/
function noteSlugFromPath(): string | null {
  return window.location.pathname.match(NOTES_PATH)?.[1] ?? null
}

// Every tab owns a real path — a URL to paste into a chat, not a ?tab=. Live is the root and
// Journal takes an optional date; the rest are their own name. The server answers all of them
// with this same shell, so keep the list in step with config/routes.rb (and with the pages
// PagePreview titles, which is what a link preview shows).
const tabPath = (tab: Tab, journalDate: string | null, noteSlug: string | null): string => {
  if (tab === 'live') return '/'
  if (tab === 'journal') return journalDate ? `/journal/${journalDate}` : '/journal'
  if (tab === 'notes') return noteSlug ? `/notes/${noteSlug}` : '/notes'
  return `/${tab}`
}

// The page names the server titles with (PagePreview::PAGES). Now that each tab has a URL worth
// bookmarking, the tab title has to follow it — a bookmark named for the page you happened to
// land on first is worse than one named for the site. Live has no entry: it IS the site.
const TAB_TITLES: Partial<Record<Tab, string>> = {
  journal: 'Journal',
  notes: 'Notes',
  stats: 'Stats',
  directory: 'Directory',
  about: 'About',
  sources: 'Sources & credits',
  tech: 'Tech & hardware',
  privacy: 'Privacy',
  letter: 'The daily letter',
}

function initialTab(): Tab {
  const path = window.location.pathname.replace(/\/$/, '').slice(1)
  if (JOURNAL_PATH.test(window.location.pathname)) return 'journal'
  if (NOTES_PATH.test(window.location.pathname)) return 'notes'
  if (TABS.includes(path as Tab)) return path as Tab
  // ?tab= is how these pages used to be addressed; old links still open the right one.
  const t = new URLSearchParams(window.location.search).get('tab')
  if (t === 'birds') return 'live' // legacy ?tab=birds link → Live
  return TABS.includes(t as Tab) ? (t as Tab) : 'live'
}

export function App({ bootstrap }: { bootstrap: Bootstrap }) {
  const [tab, setTab] = useState<Tab>(initialTab)
  // Live and Stats own INDEPENDENT periods (brief §6.2): Live watches the last day,
  // Stats reports a week by default. No shared window state.
  const [liveWin, setLiveWin] = useState<number>(24)
  const [statsWin, setStatsWin] = useState<number>(168)
  const [selected, setSelected] = useState<string | null>(null)
  // The Journal's day, owned here so it can live in the URL as a real permalink (/journal/<date>).
  // null = yesterday (the default). Seeded from the path so a shared link opens straight to that day.
  const [journalDate, setJournalDate] = useState<string | null>(journalDateFromPath)
  // The open note's slug, owned here for the same reason the Journal's date is: it belongs in the
  // URL. null = the index.
  const [noteSlug, setNoteSlug] = useState<string | null>(noteSlugFromPath)
  // The open note's own title, reported up by NotesTab, so document.title has ONE writer. Null
  // until the note has loaded — and on the index, where the section's name is the right one.
  const [noteTitle, setNoteTitle] = useState<string | null>(null)
  // The docking side-panels. Seeded from the bootstrap so a hard nav to /account or
  // /admin boots the SPA with that panel already open (deep-link).
  const [panel, setPanel] = useState<Panel>(bootstrap.open_panel ?? null)

  // The root's title, worded as the server writes it (PagePreview): the station and its place.
  const siteTitle = [bootstrap.site_name, bootstrap.place?.en].filter(Boolean).join(' — ')

  // One place owns the URL and the title: /account or /admin while a panel is open, otherwise
  // the tab's own path. replaceState — never pushState — so Back isn't trapped cycling a panel
  // open and shut.
  useEffect(() => {
    const url =
      panel === 'account' ? '/account' : panel === 'admin' ? '/admin' : tabPath(tab, journalDate, noteSlug)
    window.history.replaceState(null, '', url)
    // A note is titled by the note, not by its section: a bookmark of one reads as the piece it
    // is. Everything else takes the section's name (the server's PagePreview does the same).
    const page = (tab === 'notes' && noteSlug ? noteTitle : null) ?? TAB_TITLES[tab]
    document.title = page ? `${page} — ${bootstrap.site_name}` : siteTitle
  }, [panel, tab, journalDate, noteSlug, noteTitle, bootstrap.site_name, siteTitle])

  const user = bootstrap.current_user

  return (
    <QueryClientProvider client={queryClient}>
      <LangProvider initial={bootstrap.ui_lang}>
        <FollowProvider enabled={!!user} initial={bootstrap.favourites ?? []}>
          <Masthead
            bootstrap={bootstrap}
            tab={tab}
            // Navigating via the masthead closes any open panel — the Admin workspace and the
            // account drawer are surfaces you leave by going somewhere, not just by dismissing.
            onTab={(t) => {
              setPanel(null)
              // "Notes" in the nav means the index, not whichever note you last had open — unlike
              // the Journal, where the date you were reading is the day you want to come back to.
              if (t === 'notes') setNoteSlug(null)
              setTab(t)
            }}
            onOpenAccount={() => setPanel('account')}
          />
          <main className={shell.main}>
            {tab === 'live' && (
              <LiveTab
                onSelect={setSelected}
                windowHours={liveWin}
                onWindow={setLiveWin}
                windows={bootstrap.windows}
                mark={bootstrap.assets.mark ?? null}
              />
            )}
            {tab === 'journal' && (
              <JournalTab
                onSelect={setSelected}
                isAdmin={!!user?.admin}
                date={journalDate}
                onDate={setJournalDate}
              />
            )}
            {tab === 'notes' && <NotesTab slug={noteSlug} onSlug={setNoteSlug} onTitle={setNoteTitle} />}
            {tab === 'stats' && (
              <StatsTab onSelect={setSelected} windowHours={statsWin} onWindow={setStatsWin} />
            )}
            {tab === 'directory' && <DirectoryTab onSelect={setSelected} />}
            {tab === 'about' && <AboutTab />}
            {tab === 'sources' && <SourcesTab />}
            {tab === 'tech' && <StationPageTab slug="tech" />}
            {tab === 'privacy' && <StationPageTab slug="privacy" />}
            {tab === 'letter' && <LetterTab user={user ?? null} />}
          </main>
          <Footer
            place={bootstrap.place}
            siteName={bootstrap.site_name}
            techPage={bootstrap.tech_page}
            onNav={(t) => {
              setPanel(null)
              setTab(t)
            }}
          />
          {selected && <SpeciesModal sci={selected} onClose={() => setSelected(null)} />}
          {panel === 'account' && user && (
            <AccountPanel user={user} onClose={() => setPanel(null)} onOpenAdmin={() => setPanel('admin')} />
          )}
          {panel === 'admin' && (
            <AdminPanel
              onClose={() => setPanel(null)}
              onBack={user ? () => setPanel('account') : undefined}
            />
          )}
        </FollowProvider>
      </LangProvider>
    </QueryClientProvider>
  )
}
